import React, { useMemo, useCallback, useRef } from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { ContainerTask } from '../../../types/container'
import { AssignedTaskCard } from '../../../components/shared/AssignedTaskCard'
import { Assignment } from '../types/dispatch'

/**
 * Drag-to-reassign wrapper around the shared AssignedTaskCard.
 *
 * The card's markup lives in AssignedTaskCard so Driver Tasks and Driver
 * Management render exactly the same thing without pulling in dnd-kit. Only the
 * drag affordances (grab cursor, lift styling, the unassign hint) belong here.
 *
 * 一张卡两种拖法，共用同一个 useSortable：
 *   - 拖到别的司机卡上 = 改派（原来就有）
 *   - 拖到同一个司机同一天的另一张卡上 = 手排顺序（迁移 192）
 * 区分在 useDragDropHandlers 里做，靠的就是下面 data 里那个 sort 描述。
 * 这个组件只在 DriverCard 的 SortableContext 里渲染，所以用 useSortable 是安全的。
 */
interface DraggableAssignedTaskProps {
  container: ContainerTask
  assignment: Assignment
  taskNumber: number
  direction: {
    icon: React.ComponentType<{ className?: string }>
    text: string
  }
  getPortAbbreviation: (port: string) => string
  getWarehouseAbbreviation: (warehouse: string) => string
  formatTimeOnly: (dateString: string, allDay?: boolean) => string
  /** Opt-in: renders the details button in the card when provided. */
  onTaskDetailClick?: (task: ContainerTask) => void
  /** Marks this card as the one a search was looking for. */
  isHighlighted?: boolean
  /**
   * 这张卡属于哪个司机的哪一天，以及那一天当前从上到下的任务 id。
   * 落点判断和落库要的全部信息都在这里，handler 不必再去翻看板的 state。
   */
  sort: {
    driverId: string
    dayKey: string
    taskIds: string[]
  }
}

export const DraggableAssignedTask: React.FC<DraggableAssignedTaskProps> = React.memo(({
  container,
  assignment,
  taskNumber,
  // Deliberately unused — the card orders its endpoints itself and always draws
  // the arrow left-to-right. Kept in the props so DriverCard's call site stays
  // unchanged for its other task-card branches.
  direction: _direction,
  getPortAbbreviation,
  getWarehouseAbbreviation,
  formatTimeOnly: _formatTimeOnly,
  onTaskDetailClick,
  isHighlighted = false,
  sort
}) => {
  const elementRef = useRef<HTMLDivElement>(null)

  const { attributes, listeners, setNodeRef, transform, isDragging } = useSortable({
    id: `assignment-${assignment.id}`,
    data: {
      assignment,
      container,
      type: 'assignment',
      // 作为落点时用的信息 —— 拖到这张卡上意味着「排到这张卡这个位置」。
      sort
    }
  })

  // Combine refs for better performance
  const combinedRef = useCallback((node: HTMLDivElement | null) => {
    elementRef.current = node
    setNodeRef(node)
  }, [setNodeRef])

  // 高度优化的transform计算
  const style = useMemo(() => {
    if (!transform) {
      return isDragging ? {
        willChange: 'transform',
        zIndex: 1001, // 比container更高的z-index
        transform: 'translate3d(0, 0, 0)',
        isolation: 'isolate' as const, // 创建新的堆叠上下文
      } : {
        willChange: 'auto'
      }
    }
    return {
      transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
      willChange: 'transform',
      zIndex: 1001,
      backfaceVisibility: 'hidden' as const,
      WebkitBackfaceVisibility: 'hidden' as const,
      isolation: 'isolate' as const,
    }
  }, [transform, isDragging])

  return (
    <div className="relative">
      <AssignedTaskCard
        ref={combinedRef}
        container={container}
        taskNumber={taskNumber}
        getPortAbbreviation={getPortAbbreviation}
        getWarehouseAbbreviation={getWarehouseAbbreviation}
        onTaskDetailClick={onTaskDetailClick}
        style={style}
        className={[
          isDragging
            ? 'opacity-60 shadow-xl scale-[1.02] rotate-1 cursor-grabbing'
            : 'cursor-grab hover:bg-slate-50 hover:shadow-lg active:cursor-grabbing',
          // Search hit. Amber deliberately clashes with the cyan used for drag
          // targets and the status border colours, so the found task reads as
          // "this is the one" and not as another status. The bang modifiers are
          // needed to beat the card's own bg/border, which are equally specific.
          isHighlighted ? '!bg-amber-50 !border-amber-400 ring-2 ring-amber-400 shadow-md' : '',
        ].join(' ')}
        {...listeners}
        {...attributes}
      />

      {/* Drag Indicator —— 三种落点都说出来，否则拖起来不知道能往哪儿放 */}
      {isDragging && (
        <div className="absolute top-2 right-2 bg-blue-500 text-white px-2 py-1 rounded text-xs font-medium">
          Drop to reorder · another driver to reassign · table to unassign
        </div>
      )}
    </div>
  )
})
